import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { Organization } from "@/types/organizations";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";
import { displayErrorToast } from "@/utils/toast";

export interface CreateOrganizationWithWorkspaceAndInvitesParams {
  organization_name: string;
  workspace_name: string;
  invitee_emails?: string[];
}

export interface CreateOrganizationWithWorkspaceAndInvitesResponse extends Organization {
  default_workspace_id: number;
  default_prompt_template_id: number;
}

export const useCreateOrganizationWithWorkspaceAndInvites = () => {
  const userToken = useAuth()?.userToken ?? "";
  const queryClient = useQueryClient();

  return useMutation(
    (
      body: CreateOrganizationWithWorkspaceAndInvitesParams,
    ): Promise<CreateOrganizationWithWorkspaceAndInvitesResponse> =>
      fetch(ENDPOINTS.create_organization_with_workspace_and_invites, {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({
          organization_name: body.organization_name,
          workspace_name: body.workspace_name,
          invitee_emails:
            body.invitee_emails && body.invitee_emails.length > 0 ? body.invitee_emails : undefined,
        }),
      }).then(handleResponse),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ENDPOINTS.organizations]);
        queryClient.invalidateQueries([ENDPOINTS.workspaces]);
        queryClient.invalidateQueries([ENDPOINTS.legacy_workspaces]);
        queryClient.invalidateQueries([ENDPOINTS.workspace_invites]);
        queryClient.invalidateQueries([ENDPOINTS.me]);
      },
      onError: (error: string) => {
        displayErrorToast(error);
      },
    },
  );
};
